import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableRow,
  AppBar,
  TableCell,
  Toolbar,
  IconButton,
  TableHead,
  Button,
  TextField,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StyledDialog, StyledTableCell, StyledTableRow } from './DataDictionary.styles';
import { DICTIONARY_DATA } from './dictionaryData';

const DataDictionaryPanel = ({ open, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = useMemo(() => {
    if (!searchTerm) return DICTIONARY_DATA;
    
    const searchLower = searchTerm.toLowerCase();
    return DICTIONARY_DATA.filter(row => 
      row.inspectorName.toLowerCase().includes(searchLower) ||
      row.fieldName.toLowerCase().includes(searchLower) ||
      row.definition.toLowerCase().includes(searchLower) ||
      row.dataType.toLowerCase().includes(searchLower) ||
      row.uiGrouping.toLowerCase().includes(searchLower)
    );
  }, [searchTerm]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullScreen
      maxWidth="xl"
    >
      <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="relative" color="primary">
          <Toolbar variant="dense">
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Data Dictionary
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search in all columns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell className="header">INSPECTOR Name</StyledTableCell>
                <StyledTableCell className="header">Field Name</StyledTableCell>
                <StyledTableCell className="header">Definition</StyledTableCell>
                <StyledTableCell className="header">Data Type</StyledTableCell>
                <StyledTableCell className="header">INSPECTOR UI Grouping</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, index) => (
                <StyledTableRow key={index} className="dictionary-row">
                  <TableCell className="label-cell">{row.inspectorName}</TableCell>
                  <TableCell>{row.fieldName}</TableCell>
                  <TableCell>{row.definition}</TableCell>
                  <TableCell>{row.dataType}</TableCell>
                  <TableCell>{row.uiGrouping}</TableCell>
                </StyledTableRow>
              ))}
              {filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No matching records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2">
            Showing {filteredData.length} of {DICTIONARY_DATA.length} entries
          </Typography>
          <Button 
            variant="contained" 
            onClick={onClose}
            color="primary"
          >
            Close
          </Button>
        </Paper>
      </Paper>
    </StyledDialog>
  );
};

DataDictionaryPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default DataDictionaryPanel;
